import axios from 'axios';
import API_URL from './Backendurl';

function clearStoredAuth() {
  localStorage.removeItem('token');
  delete axios.defaults.headers.common.Authorization;
}

// Create axios instance
const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Don't set Content-Type for FormData, let browser set it with boundary
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const url = String(error.config?.url || '');
      const isPublicAuth =
        url.includes('/auth/login') ||
        url.includes('/auth/register') ||
        url.includes('/auth/register-email') ||
        url.includes('/auth/register-wallet');

      if (!isPublicAuth) {
        clearStoredAuth();
        const onLoginPage =
          window.location.pathname === '/login' ||
          window.location.pathname.startsWith('/login');
        if (!onLoginPage) {
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

export default api;

// API endpoints
export const API_ENDPOINTS = {
  // Authentication
  AUTH: {
    LOGIN: '/auth/login',
    REGISTER: '/auth/register',
    ME: '/auth/me',
  },
  
  // Jobs
  JOBS: {
    LIST: '/jobs',
    DETAIL: (id) => `/jobs/${id}`,
    CREATE: '/jobs',
    APPLY: (id) => `/jobs/${id}/apply`,
    UPDATE: (id) => `/jobs/${id}`,
    UPDATE_STATUS: (id) => `/jobs/${id}/status`,
    MY_JOBS: '/jobs/my-jobs',
    MY_FREELANCE_JOBS: '/jobs/my-freelance-jobs',
    MY_FULLTIME_JOBS: '/jobs/my-fulltime-jobs',
    MY_OTHER_JOBS: '/jobs/my-other-jobs',
    APPLICATIONS: (id) => `/jobs/${id}/applications`,
    APPROVE_APPLICATION: (id, appId) => `/jobs/${id}/applications/${appId}/approve`,
    REJECT_APPLICATION: (id, appId) => `/jobs/${id}/applications/${appId}/reject`,
    ARCHIVE: (id) => `/jobs/${id}/archive`,
    UNARCHIVE: (id) => `/jobs/${id}/unarchive`,
    HIDE: (id) => `/jobs/${id}/hide`,
    UNHIDE: (id) => `/jobs/${id}/unhide`,
    DELETE: (id) => `/jobs/${id}`,
  },
  
  // Gigs
  GIGS: {
    LIST: '/gigs',
    DETAIL: (id) => `/gigs/${id}`,
    CREATE: '/gigs',
    UPDATE: (id) => `/gigs/${id}`,
    ORDER: (id) => `/gigs/${id}/order`,
    MY_GIGS: '/gigs/my-gigs',
    MY_ORDERED_GIGS: '/gigs/my-ordered-gigs',
    ORDERS: (id) => `/gigs/${id}/orders`,
    APPROVE_ORDER: (id, orderId) => `/gigs/${id}/orders/${orderId}/approve`,
    REJECT_ORDER: (id, orderId) => `/gigs/${id}/orders/${orderId}/reject`,
    ARCHIVE: (id) => `/gigs/${id}/archive`,
    UNARCHIVE: (id) => `/gigs/${id}/unarchive`,
    DELETE: (id) => `/gigs/${id}`,
  },
  
  // Chats
  CHATS: {
    LIST: '/chats',
    DETAIL: (id) => `/chats/${id}`,
    MESSAGES: (id) => `/chats/${id}/messages`,
    SEND_MESSAGE: (id) => `/chats/${id}/messages`,
    UPDATE_STATUS: (id) => `/chats/${id}/status`,
    UPDATE_WORKFLOW_STATUS: (id) => `/chats/${id}/workflow-status`,
    UPDATE_PRICE: (id) => `/chats/${id}/price`,
    ESCROW_DEPOSIT: (id) => `/chats/${id}/escrow/deposit`,
    ESCROW_IN_PROGRESS: (id) => `/chats/${id}/escrow/in-progress`,
    ESCROW_COMPLETE: (id) => `/chats/${id}/escrow/complete`,
    ESCROW_DISBURSE: (id) => `/chats/${id}/escrow/disburse`,
    ESCROW_CONFIRM: (id) => `/chats/${id}/escrow/confirm`,
    WORKFLOW: (id) => `/chats/${id}/workflow`,
    RATING: (id) => `/chats/${id}/rating`,
    UNREAD_COUNT: '/chats/unread-count',
    SEARCH_USERS: '/chats/users/search',
    ADD_PARTICIPANT: (id) => `/chats/${id}/participants`,
    REMOVE_PARTICIPANT: (id, userId) => `/chats/${id}/participants/${userId}`,
    DOWNLOAD: (messageId, attachmentIndex) => `/chats/download/${messageId}/${attachmentIndex}`,
  },
  
  // Notifications
  NOTIFICATIONS: {
    LIST: '/notifications',
    UNREAD_COUNT: '/notifications/unread-count',
    MARK_READ: (id) => `/notifications/${id}/read`,
    MARK_ALL_READ: '/notifications/read-all',
    DELETE: (id) => `/notifications/${id}`,
  },
  
  // Wallet
  WALLET: {
    BALANCE: '/wallet/balance',
    TRANSACTIONS: '/wallet/transactions',
    TRANSACTIONS_ONCHAIN: '/wallet/transactions/onchain',
    DEPOSIT: '/wallet/deposit',
    WITHDRAW: '/wallet/withdraw',
    ESCROW_DETAILS: '/wallet/escrow/details',
    ESCROW_RELEASE: '/wallet/escrow/release',
    CONNECT: '/wallet/connect',
    DISCONNECT: '/wallet/disconnect',
  },
  
  // Users
  USERS: {
    PROFILE: (username) => `/users/profile/${username}`,
    ME: '/users',
    UPDATE_PROFILE: '/users/profile',
    UPDATE_SETTINGS: '/users/settings',
    CHANGE_PASSWORD: '/users/password',
    RATINGS: '/users/ratings',
    PROFILE_RATINGS: (username) => `/users/profile/${username}/ratings`,
    RATE_USER: (userId) => `/users/${userId}/ratings`,
    ACTIVITY: '/users/activity',
    STATS: '/users/stats',
    DASHBOARD_STATS: '/users/dashboard/stats',
    SWITCH_ROLE: '/users/switch-role',
    SEARCH: '/users/search',
  },
  
  // Governance
  GOVERNANCE: {
    LIST: '/governance',
    DETAIL: (id) => `/governance/${id}`,
    CREATE: '/governance',
    VOTE: (id) => `/governance/${id}/vote`,
    COMMENTS: (id) => `/governance/${id}/comments`,
    DISPUTE_MESSAGES: (id) => `/governance/${id}/dispute/messages`,
    CHAT_PROPOSAL: (chatId) => `/governance/chat/${chatId}/proposal`,
    RESOLVE: (id) => `/governance/${id}/resolve`,
    RESOLVE_CONFIRM: (id) => `/governance/${id}/resolve/confirm`,
    DISPUTE_ON_CHAIN_AUTH: (id) => `/governance/${id}/dispute/on-chain-auth`,
    DISPUTE_RELAY_RESOLVE: (id) => `/governance/${id}/dispute/relay-resolve`,
    SETTLEMENT: (id) => `/governance/${id}/settlement`,
    SETTLEMENT_APPROVE: (id) => `/governance/${id}/settlement/approve`,
    SETTLEMENT_RESOLVE: (id) => `/governance/${id}/settlement/resolve`,
    SETTLEMENT_RESOLVE_CONFIRM: (id) => `/governance/${id}/settlement/resolve/confirm`,
    DISPUTE_PAYOUT_CONFIG: '/governance/dispute-payout-config',
    LEADERBOARD: '/governance/leaderboard',
    ELIGIBLE_WORK: '/governance/eligible-work',
    ELIGIBILITY: '/governance/eligibility',
    ACTIVITY_METRICS: '/governance/metrics/activity',
    CONTRACT_HISTORY: (id) => `/governance/${id}/contract-history`,
  },
  
  // Referrals
  REFERRAL: {
    INFO: '/referral',
    WITHDRAW: '/referral/withdraw',
    VALIDATE: (code) => `/referral/validate/${code}`,
    STATS: '/referral',
  },

  // Deployer
  DEPLOYER: {
    TRANSACTIONS: '/deployer/transactions',
    LOG_TRANSACTION: '/deployer/transactions',
    CONFIG: '/deployer/config',
    UPDATE_CONFIG: '/deployer/config',
    DAO_POOL_BALANCES: '/deployer/dao-pool-balances'
  },

  // Search
  SEARCH: {
    UNIFIED: '/search',
  },

  // Staking
  STAKING: {
    POOL_INFO: '/staking/pool-info',
    USER_STAKES: '/staking/user-stakes',
    ALL_STAKES: '/staking/all-stakes',
    RECORD_STAKE: '/staking/record-stake',
    RECORD_UNSTAKE: '/staking/record-unstake',
    RECORD_CLAIM: '/staking/record-claim',
    SYNC_STAKER: '/staking/sync-staker',
    STATS: '/staking/stats',
  },

  // Blogs
  BLOGS: {
    LIST: '/blogs',
    DETAIL: (id) => `/blogs/${id}`,
    CREATE: '/blogs',
    UPDATE: (id) => `/blogs/${id}`,
    DELETE: (id) => `/blogs/${id}`,
    LIKE: (id) => `/blogs/${id}/like`,
    SAVE: (id) => `/blogs/${id}/save`,
    COMMENT: (id) => `/blogs/${id}/comment`,
    COMMENT_LIKE: (id, commentId) => `/blogs/${id}/comment/${commentId}/like`,
    VIEW: (id) => `/blogs/${id}/view`,
    IMPRESSION: (id) => `/blogs/${id}/impression`,
    MY_BLOGS: '/blogs/user/my-blogs',
    EARNINGS: '/blogs/user/earnings',
    WITHDRAW: '/blogs/user/withdraw',
    STATS: '/blogs/user/stats',
    FOLLOW: (userId) => `/blogs/user/follow/${userId}`,
    SAVED: '/blogs/user/saved',
    FOLLOWING: '/blogs/user/following',
    // Admin
    ADMIN_ALL: '/blogs/admin/all',
    ADMIN_FEATURE: (id) => `/blogs/admin/${id}/feature`,
    ADMIN_SPONSOR: (id) => `/blogs/admin/${id}/sponsor`,
    ADMIN_PRIORITY: (id) => `/blogs/admin/${id}/priority`,
    ADMIN_DELETE: (id) => `/blogs/admin/${id}`,
    EARNINGS_CONFIG: '/blogs/earnings-config',
  },

  // Email Subscriptions
  EMAIL_SUBSCRIPTIONS: {
    SUBSCRIBE: '/email-subscriptions',
    LIST: '/email-subscriptions',
    DELETE: (id) => `/email-subscriptions/${id}`,
    DELETE_MULTIPLE: '/email-subscriptions',
    EXPORT: '/email-subscriptions/export',
  },
};
